import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import InsertChartOutlinedTwoToneIcon from "@mui/icons-material/InsertChartOutlinedTwoTone";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import SunburstChart from "./SunburstChart";
import BarChart from "./BarChart";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import TimeSelector from "../Dashboard/components/TimeSelector";
import Activities from "../Dashboard/components/activities";
import OverviewCards from "../../Screens/Dashboard/components/overviewCards"; // ← naya import

// ── SHIMMER COMPONENT ──
const Shimmer = ({
  width = "100%",
  height = "20px",
  borderRadius = "8px",
  sx = {},
}) => (
  <Box
    sx={{
      width,
      height,
      borderRadius,
      background:
        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
      flexShrink: 0,
      "@keyframes shimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" },
      },
      ...sx,
    }}
  />
);

const LegendItem = ({ color, label, isHeading }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
    <Box
      sx={{
        width: isHeading ? 9 : 8,
        height: isHeading ? 11 : 8,
        borderRadius: "50%",
        backgroundColor: color,
      }}
    />
    <Typography
      sx={{
        fontFamily: "Roboto",
        fontSize: isHeading ? "12px" : "11px",
        fontWeight: 400,
        color: isHeading ? "#000" : "#353535",
        lineHeight: 0,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const CountText = ({
  color,
  highlightCount,
  totalCount,
  highlightedText,
  simpleText,
  lastText,
}) => (
  <Box
    sx={{
      display: "flex",
      padding: "0 8px",
      alignItems: "center",
      alignSelf: "stretch",
      gap: 1.5,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: "6px",
        height: "100%",
        borderRadius: "4px",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 400 }}>
          {highlightCount || 0}
        </Typography>
        {totalCount !== undefined && (
          <Typography
            sx={{ fontSize: "12px", fontWeight: 400, color: "#9e9e9e" }}
          >
            /{totalCount}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
        <span>{highlightedText}</span>
        <span style={{ color: "#9e9e9e" }}>
          {highlightedText ? ` / ${simpleText}` : simpleText}
        </span>
      </Typography>
      {lastText && (
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#000" }}>
          {lastText}
        </Typography>
      )}
    </Box>
  </Box>
);

const overviewCardArr = [
  { color: "#64AE68", count: "0", text: "Total Jobs", key: "total_jobs" },
  {
    color: "#3F95EA",
    count: "0",
    text: "Total Candidates",
    key: "total_candidates",
  },
  {
    color: "#FFA800",
    count: "0",
    text: "Assigned Admins",
    key: "total_admins",
  },
  {
    color: "#AE64BB",
    count: "0",
    text: "Assigned TA Managers",
    key: "total_ta",
  },
  {
    color: "#F78426",
    count: "0",
    text: "Assigned Recruiters",
    key: "total_recruiters",
  },
];

const MyDashboard = () => {
  const [jobCount, setJobCount] = useState({});
  const [candidatesCount, setCandidatesCount] = useState({});
  const [overView, setOverView] = useState(overviewCardArr);
  const [jobsFilterOption, setJobsFilterOption] = useState({
    label: "Last Week",
    value: "Last 7 Days",
  });
  const [loading, setLoading] = useState({
    overView: false,
    jobs: false,
    candidates: false,
    activities: false,
  });
  const [activities, setActivities] = useState([]);

  const nav = useNavigate();
  const handleTimeFilters = (data) => setJobsFilterOption(data);
  const handleNavigation = (path) => nav(path);

  const fetchOverViewCount = async () => {
    try {
      setLoading((prev) => ({ ...prev, overView: true }));
      const response = await axios.get("my-dashboard/overview-data", {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      const data = response.data.message.result;
      const arr = [];
      for (let i = 0; i < overviewCardArr.length; i++) {
        const overViewItem = Object.keys(data).find(
          (key) => key === overviewCardArr[i].key,
        );
        if (overViewItem)
          arr.push({ ...overviewCardArr[i], count: data[overViewItem] });
      }
      setOverView(arr);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, overView: false }));
    }
  };

  const fetchDashboardJobCount = async () => {
    try {
      setLoading((prev) => ({ ...prev, jobs: true }));
      const response = await axios.get("/my-dashboard/job-count", {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
        params: { filterPeriod: jobsFilterOption.label },
      });
      const data = response.data.message[0];
      setJobCount({
        activeJobs: {
          existingJobs: data.activeJobs.existingJobs || 0,
          newJobs: data.activeJobs.newJobs || 0,
          totalActiveJobs:
            (data.activeJobs.existingJobs || 0) +
            (data.activeJobs.newJobs || 0),
        },
        inactiveJobs: {
          cancelledJobs: data.inactiveJobs.cancelledJobs || 0,
          closedJobs: data.inactiveJobs.closedJobs || 0,
          fullJobs: data.inactiveJobs.fullJobs || 0,
          onHoldJobs: data.inactiveJobs.onHoldJobs || 0,
          totalInactiveJobs:
            (data.inactiveJobs.cancelledJobs || 0) +
            (data.inactiveJobs.closedJobs || 0) +
            (data.inactiveJobs.fullJobs || 0) +
            (data.inactiveJobs.onHoldJobs || 0),
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, jobs: false }));
    }
  };

  const fetchDashboardCandidates = async () => {
    try {
      setLoading((prev) => ({ ...prev, candidates: true }));
      const response = await axios.get("/my-dashboard/candidate-count", {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setCandidatesCount(response.data.message[0].result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, candidates: false }));
    }
  };

  const fetchDashboardActivities = async () => {
    try {
      setLoading((prev) => ({ ...prev, activities: true }));
      const response = await axios.get("/my-dashboard/recent-activities", {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setActivities(response.data.message.result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading((prev) => ({ ...prev, activities: false }));
    }
  };

  useEffect(() => {
    fetchDashboardJobCount();
  }, [jobsFilterOption]);
  useEffect(() => {
    fetchOverViewCount();
    fetchDashboardCandidates();
    fetchDashboardActivities();
  }, []);

  return (
    <Box
      sx={{
        background: "#E3F2FD",
        height: "100%",
        maxHeight: "100vh",
        overflowY: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        boxSizing: "border-box",
        borderRadius: "16px 0 0 0",
        width: "100%",
        position: "relative",
      }}
    >
      {/* ── OVERVIEW CARDS — alag component ── */}
      <OverviewCards overView={overView} loading={loading.overView} />

      {/* ── BOTTOM THREE PANELS ── */}
      <Box sx={{ gap: 2, flex: 1, display: "flex", overflow: "hidden" }}>
        {/* ── JOBS PANEL ── */}
        <Box
          sx={{
            background: "#FFF",
            height: "100%",
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden",
            borderRadius: 2.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#F6F6F6",
              px: 1.5,
              py: 1,
              borderRadius: "10px 10px 0 0",
            }}
          >
            <Typography
              sx={{ fontSize: "16px", color: "#242437", fontWeight: "600" }}
            >
              Total Jobs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimeSelector
                initialValue={jobsFilterOption}
                handleFilters={handleTimeFilters}
              />
              <Button
                variant="text"
                onClick={() => handleNavigation("/dashboard?data=Jobs")}
                sx={{
                  width: "32px",
                  minWidth: "32px",
                  padding: 0,
                  height: "32px",
                  background: "#FFF",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="20px"
                  viewBox="0 -960 960 960"
                  width="20px"
                  fill="#006CB7"
                >
                  <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                </svg>
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              flex: 1,
              height: "100%",
              display: "flex",
              gap: 1.5,
              flexDirection: "column",
              justifyContent: "space-between",
              overflow: "hidden",
            }}
          >
            {loading.jobs ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%",
                  p: 2,
                }}
              >
                {/* Chart + Legend row — screenshot se match */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                  }}
                >
                  {/* Donut chart shimmer */}
                  <Box
                    sx={{
                      position: "relative",
                      flexShrink: 0,
                      width: "140px",
                      height: "140px",
                    }}
                  >
                    <Shimmer width="130px" height="130px" borderRadius="50%" />
                    {/* Inner white circle — donut hole */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "78px",
                        height: "78px",
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                      }}
                    />
                  </Box>

                  {/* Legend shimmer — 2 columns */}
                  <Box sx={{ display: "flex", gap: 3, flex: 1 }}>
                    {/* Column 1 — Inactive Jobs */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.2,
                      }}
                    >
                      {/* Heading */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Shimmer
                          width="10px"
                          height="10px"
                          borderRadius="50%"
                        />
                        <Shimmer
                          width="80px"
                          height="13px"
                          borderRadius="4px"
                        />
                      </Box>
                      {/* Items */}
                      {["Full", "Cancelled", "On-Hold", "Closed"].map(
                        (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Shimmer
                              width="8px"
                              height="8px"
                              borderRadius="50%"
                            />
                            <Shimmer
                              width="55px"
                              height="10px"
                              borderRadius="4px"
                            />
                          </Box>
                        ),
                      )}
                    </Box>

                    {/* Column 2 — Active Jobs */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.2,
                      }}
                    >
                      {/* Heading */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Shimmer
                          width="10px"
                          height="10px"
                          borderRadius="50%"
                        />
                        <Shimmer
                          width="70px"
                          height="13px"
                          borderRadius="4px"
                        />
                      </Box>
                      {/* Items */}
                      {["New", "Existing"].map((_, i) => (
                        <Box
                          key={i}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Shimmer
                            width="8px"
                            height="8px"
                            borderRadius="50%"
                          />
                          <Shimmer
                            width="45px"
                            height="10px"
                            borderRadius="4px"
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* ✅ Count items shimmer — neeche 4 rows */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  {[...Array(4)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        height: "40px",
                      }}
                    >
                      <Shimmer width="6px" height="40px" borderRadius="4px" />
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5,
                          }}
                        >
                          <Shimmer
                            width="40px"
                            height="20px"
                            borderRadius="4px"
                          />
                          <Shimmer
                            width="28px"
                            height="13px"
                            borderRadius="4px"
                          />
                        </Box>
                        <Shimmer width="60%" height="12px" borderRadius="4px" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    overflow: "hidden",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  {!jobCount?.inactiveJobs?.totalInactiveJobs &&
                  !jobCount?.activeJobs?.totalActiveJobs ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        width: "100%",
                        color: "#ccc",
                        pt: 0.5,
                        px: 3,
                      }}
                    >
                      <DataUsageOutlinedIcon sx={{ fontSize: "18vh" }} />
                      <Typography sx={{ fontSize: "1.6vh" }}>
                        No Data Available
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flex: 1,
                        flexDirection: "row",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: "12vw",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <SunburstChart jobCount={jobCount} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-evenly",
                          alignItems: "flex-start",
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ width: "105px" }}>
                          <LegendItem
                            color="#006CB7"
                            label="Inactive Jobs"
                            isHeading
                          />
                          <LegendItem color="#247bd2" label="Full" />
                          <LegendItem color="#3F95EA" label="Cancelled" />
                          <LegendItem color="#5d95be" label="On-Hold" />
                          <LegendItem color="#99C4E2" label="Closed" />
                        </Box>
                        <Box sx={{ width: "92px" }}>
                          <LegendItem
                            color="#27BB2E"
                            label="Active Jobs"
                            isHeading
                          />
                          <LegendItem color="#2ECC40" label="New" />
                          <LegendItem color="#A9E4AB" label="Existing" />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: "20px",
                    alignSelf: "stretch",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ height: "40px" }}>
                    <CountText
                      color="#3F95EA"
                      highlightCount={jobCount?.inactiveJobs?.cancelledJobs}
                      totalCount={jobCount?.inactiveJobs?.totalInactiveJobs}
                      highlightedText="Cancelled Jobs"
                      simpleText="Inactive Jobs"
                    />
                  </Box>
                  <Box sx={{ height: "40px" }}>
                    <CountText
                      color="#99C4E2"
                      highlightCount={jobCount?.inactiveJobs?.closedJobs}
                      totalCount={jobCount?.inactiveJobs?.totalInactiveJobs}
                      highlightedText="Closed Jobs"
                      simpleText="Inactive Jobs"
                    />
                  </Box>
                  <Box sx={{ height: "40px" }}>
                    <CountText
                      color="#4AEC52"
                      highlightCount={jobCount?.activeJobs?.newJobs}
                      totalCount={jobCount?.activeJobs?.totalActiveJobs}
                      highlightedText="New Jobs"
                      simpleText="Active Jobs"
                    />
                  </Box>
                  <Box sx={{ height: "40px" }}>
                    <CountText
                      color="#A9E4AB"
                      highlightCount={jobCount?.activeJobs?.existingJobs}
                      totalCount={jobCount?.activeJobs?.totalActiveJobs}
                      highlightedText="Existing Jobs"
                      simpleText="Active Jobs"
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* ── CANDIDATES PANEL ── */}
        <Box
          sx={{
            background: "#FFF",
            height: "100%",
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#F6F6F6",
              px: 1.5,
              py: 1,
              borderRadius: "10px 10px 0 0",
            }}
          >
            <Typography
              sx={{ fontSize: "16px", color: "#242437", fontWeight: "600" }}
            >
              Total Candidates
            </Typography>
            <Button
              variant="text"
              onClick={() => handleNavigation("/dashboard?data=Candidates")}
              sx={{
                width: "32px",
                minWidth: "32px",
                padding: 0,
                height: "32px",
                background: "#FFF",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20px"
                viewBox="0 -960 960 960"
                width="20px"
                fill="#006CB7"
              >
                <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
              </svg>
            </Button>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              gap: 3,
            }}
          >
            {loading.candidates ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 2.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Shimmer width="70px" height="13px" borderRadius="4px" />
                    <Shimmer width="25%" height="28px" borderRadius="4px" />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Shimmer width="70px" height="13px" borderRadius="4px" />
                    <Shimmer width="72%" height="28px" borderRadius="4px" />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, pl: "80px" }}>
                    {[...Array(4)].map((_, i) => (
                      <Shimmer
                        key={i}
                        width="30px"
                        height="11px"
                        borderRadius="4px"
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Shimmer width="60px" height="13px" borderRadius="4px" />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        height: "60px",
                      }}
                    >
                      <Shimmer width="6px" height="60px" borderRadius="4px" />
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5,
                          }}
                        >
                          <Shimmer
                            width="45px"
                            height="20px"
                            borderRadius="4px"
                          />
                          {i !== 0 && (
                            <Shimmer
                              width="32px"
                              height="13px"
                              borderRadius="4px"
                            />
                          )}
                        </Box>
                        <Shimmer width="55%" height="12px" borderRadius="4px" />
                        <Shimmer width="38%" height="12px" borderRadius="4px" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ minHeight: 0, overflow: "hidden", height: "100%" }}>
                  {!candidatesCount?.employerPortal?.associatedWithJob &&
                  !candidatesCount?.employerPortal?.notAssociatedWithJob &&
                  !candidatesCount?.jobezePortal ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                        color: "#ccc",
                        pt: 4,
                      }}
                    >
                      <InsertChartOutlinedTwoToneIcon
                        sx={{ fontSize: "18vh", transform: "rotate(90deg)" }}
                      />
                      <Typography sx={{ fontSize: "1.6vh" }}>
                        No Data Available
                      </Typography>
                    </Box>
                  ) : (
                    <BarChart candidatesCount={candidatesCount} />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: "15px",
                    alignSelf: "stretch",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ height: "60px", width: "100%" }}>
                    <CountText
                      color="#006CB7"
                      highlightCount={candidatesCount?.jobezePortal}
                      simpleText="Job Associated Candidates"
                      lastText="Jobeze Portal"
                    />
                  </Box>
                  <Box sx={{ height: "60px", width: "100%" }}>
                    <CountText
                      color="#3F95EA"
                      highlightCount={
                        candidatesCount?.employerPortal?.associatedWithJob
                      }
                      totalCount={
                        candidatesCount?.employerPortal?.associatedWithJob +
                        candidatesCount?.employerPortal?.notAssociatedWithJob
                      }
                      simpleText="Job Associated Candidates"
                      lastText="Employer Portal"
                    />
                  </Box>
                  <Box sx={{ height: "60px", width: "100%" }}>
                    <CountText
                      color="rgba(63,149,234,0.6)"
                      highlightCount={
                        candidatesCount?.employerPortal?.notAssociatedWithJob
                      }
                      totalCount={
                        candidatesCount?.employerPortal?.associatedWithJob +
                        candidatesCount?.employerPortal?.notAssociatedWithJob
                      }
                      simpleText="Candidates Job Not Associated"
                      lastText="Employer Portal"
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* ── ACTIVITIES PANEL ── */}
        <Box
          sx={{
            background: "#FFF",
            height: "100%",
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2.5,
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "#242437",
              fontWeight: "600",
              background: "#F6F6F6",
              px: 1.5,
              py: 1.65,
              borderRadius: "10px 10px 0 0",
              flexShrink: 0,
            }}
          >
            Recent Activities
          </Typography>

          {loading.activities ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                px: 1.5,
                pb: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflowY: "hidden",
              }}
            >
              {[...Array(7)].map((_, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Shimmer width="36px" height="36px" borderRadius="50%" />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                    }}
                  >
                    <Shimmer width="45%" height="12px" borderRadius="4px" />
                    <Shimmer width="80%" height="14px" borderRadius="4px" />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                px: 1.5,
                pb: 1.5,
                overflowY: "auto",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              <Activities
                activityData={activities}
                loading={loading.activities}
                onItemClick={(item) => {
                  console.log("Clicked Activity:", item);
                  if (item?.jobId) nav(`/jobs/details/${item.jobId}`);
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MyDashboard;
